import type { HttpClient, RequestOptions } from '../client.js';
import {
  type RpcResponse,
  type SolanaAccountInfo,
  type SolanaTransactionData,
  type SolanaSignatureInfo,
  getSolanaBalanceParamsSchema,
  getSolanaAccountInfoParamsSchema,
  getSolanaTokenAccountsParamsSchema,
  getSolanaTransactionParamsSchema,
  getSolanaSignaturesParamsSchema,
  getSolanaTokenSupplyParamsSchema,
  solanaBlockhashSchema,
  rpcRequestSchema,
} from '@spectrum-nodes/api-spec/models';
import { LAMPORTS_PER_SOL, SPL_TOKEN_PROGRAM_ID } from '@spectrum-nodes/api-spec/constants';
import { z } from 'zod';
import type { SolanaTokenAccountsResult, SignaturesFilter } from '../types/index.js';
import { ApiError } from '../types/errors.js';
import { parseParams } from '../internals/validation.js';

const blockhashWrapperSchema = z.object({ blockhash: solanaBlockhashSchema }).strict();

/**
 * Solana-specific convenience methods.
 * All calls proxy through the Spectrum API JSON-RPC `rpcProxy` method.
 */
export class SolanaNamespace {
  constructor(private readonly client: HttpClient) {}

  // --- Convenience methods ---

  /**
   * Get the current slot (equivalent to block height).
   */
  async getSlot(options?: RequestOptions): Promise<number> {
    return this.send<number>('getSlot', [], options);
  }

  /**
   * Get the current block height.
   */
  async getBlockHeight(options?: RequestOptions): Promise<number> {
    return this.send<number>('getBlockHeight', [], options);
  }

  /**
   * Get SOL balance for an address in SOL (not lamports).
   */
  async getBalance(address: string, options?: RequestOptions): Promise<number> {
    const validated = parseParams(getSolanaBalanceParamsSchema, { address });
    const result = await this.send<{ value: number }>('getBalance', [validated.address], options);
    return result.value / LAMPORTS_PER_SOL;
  }

  /**
   * Get raw SOL balance in lamports.
   */
  async getBalanceLamports(address: string, options?: RequestOptions): Promise<number> {
    const validated = parseParams(getSolanaBalanceParamsSchema, { address });
    const result = await this.send<{ value: number }>('getBalance', [validated.address], options);
    return result.value;
  }

  /**
   * Get account info for a Solana address.
   */
  async getAccountInfo(
    address: string,
    encoding: 'base64' | 'jsonParsed' = 'jsonParsed',
    options?: RequestOptions,
  ): Promise<SolanaAccountInfo | null> {
    const validated = parseParams(getSolanaAccountInfoParamsSchema, { address, encoding });
    const result = await this.send<{ value: SolanaAccountInfo | null }>(
      'getAccountInfo',
      [validated.address, { encoding: validated.encoding }],
      options,
    );
    return result.value;
  }

  /**
   * Get all SPL token accounts for a wallet.
   */
  async getTokenAccountsByOwner(
    owner: string,
    opts?: { mint?: string; programId?: string },
    options?: RequestOptions,
  ): Promise<SolanaTokenAccountsResult['value']> {
    const validated = parseParams(getSolanaTokenAccountsParamsSchema, {
      owner,
      mint: opts?.mint,
      programId: opts?.mint ? undefined : (opts?.programId ?? SPL_TOKEN_PROGRAM_ID),
    });
    const filter = validated.mint ? { mint: validated.mint } : { programId: validated.programId };

    const result = await this.send<SolanaTokenAccountsResult>(
      'getTokenAccountsByOwner',
      [validated.owner, filter, { encoding: 'jsonParsed' }],
      options,
    );
    return result.value;
  }

  /**
   * Get a confirmed transaction by signature.
   */
  async getTransaction(
    signature: string,
    options?: RequestOptions,
  ): Promise<SolanaTransactionData | null> {
    const validated = parseParams(getSolanaTransactionParamsSchema, { signature });
    return this.send<SolanaTransactionData | null>(
      'getTransaction',
      [validated.signature, { encoding: 'jsonParsed', maxSupportedTransactionVersion: 0 }],
      options,
    );
  }

  /**
   * Get recent transaction signatures for an address.
   */
  async getSignaturesForAddress(
    address: string,
    filter?: SignaturesFilter,
    options?: RequestOptions,
  ): Promise<SolanaSignatureInfo[]> {
    const validated = parseParams(getSolanaSignaturesParamsSchema, {
      address,
      limit: filter?.limit,
      before: filter?.before,
      until: filter?.until,
      commitment: filter?.commitment,
    });
    const { address: validAddress, ...rest } = validated;
    const definedFilter = Object.fromEntries(
      Object.entries(rest).filter(([, v]) => v !== undefined),
    );
    const params: unknown[] = [validAddress];
    if (Object.keys(definedFilter).length > 0) {
      params.push(definedFilter);
    }
    return this.send<SolanaSignatureInfo[]>('getSignaturesForAddress', params, options);
  }

  /**
   * Get the latest blockhash.
   */
  async getLatestBlockhash(
    options?: RequestOptions,
  ): Promise<{ blockhash: string; lastValidBlockHeight: number }> {
    const result = await this.send<{ value: { blockhash: string; lastValidBlockHeight: number } }>(
      'getLatestBlockhash',
      [],
      options,
    );
    return result.value;
  }

  /**
   * Get minimum balance required for rent exemption.
   */
  async getMinimumBalanceForRentExemption(
    dataSize: number,
    options?: RequestOptions,
  ): Promise<number> {
    return this.send<number>('getMinimumBalanceForRentExemption', [dataSize], options);
  }

  /**
   * Check if a blockhash is still valid.
   */
  async isBlockhashValid(blockhash: string, options?: RequestOptions): Promise<boolean> {
    const validated = parseParams(blockhashWrapperSchema, { blockhash });
    const result = await this.send<{ value: boolean }>(
      'isBlockhashValid',
      [validated.blockhash],
      options,
    );
    return result.value;
  }

  /**
   * Get the current epoch info.
   */
  async getEpochInfo(options?: RequestOptions): Promise<{
    epoch: number;
    slotIndex: number;
    slotsInEpoch: number;
    absoluteSlot: number;
    blockHeight: number;
  }> {
    return this.send('getEpochInfo', [], options);
  }

  /**
   * Get token supply for an SPL token mint.
   */
  async getTokenSupply(
    mint: string,
    options?: RequestOptions,
  ): Promise<{ amount: string; decimals: number; uiAmount: number; uiAmountString: string }> {
    const validated = parseParams(getSolanaTokenSupplyParamsSchema, { mint });
    const result = await this.send<{
      value: { amount: string; decimals: number; uiAmount: number; uiAmountString: string };
    }>('getTokenSupply', [validated.mint], options);
    return result.value;
  }

  // --- Raw RPC access ---

  /**
   * Send any Solana JSON-RPC method directly.
   */
  async request(
    req: { method: string; params?: unknown[] },
    options?: RequestOptions,
  ): Promise<RpcResponse> {
    const validated = parseParams(rpcRequestSchema, { method: req.method, params: req.params });
    return this.client.rpcProxy(
      'solana',
      { method: validated.method, params: validated.params ?? [], id: 1 },
      { ...options, cacheTtl: 0 },
    ) as Promise<RpcResponse>;
  }

  // --- Internal helpers ---

  private async send<T>(method: string, params: unknown[], options?: RequestOptions): Promise<T> {
    const response = (await this.client.rpcProxy(
      'solana',
      { method, params, id: 1 },
      { ...options, cacheTtl: 0 },
    )) as RpcResponse;

    if ('error' in response && response.error) {
      const err = response.error as { code: number; message: string };
      throw new ApiError(
        `Solana RPC error: ${err.message} (code: ${err.code})`,
        502,
        this.client.endpointUrl(),
        response.error,
      );
    }

    return (response as { result: T }).result;
  }
}
